import React from 'react'
import { AppContext } from '../context/AppContext'

/* ============================================================
   Shared utilities: icons (Lucide stroke), i18n strings, data
   Exposes: Icon, I18N, useT, FAKE
   ============================================================ */

// -- Icon set — Lucide stroke style (fill="none", stroke=currentColor, strokeWidth=1.6)
// Each value is JSX content placed inside an <svg> wrapper by the Icon component.
const ICONS = {
  search:    <><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>,
  bell:      <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></>,
  help:      <><circle cx="12" cy="12" r="10"/><path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></>,
  menu:      <path d="M4 6h16M4 12h16M4 18h16"/>,
  home:      <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-7h-6v7H4a1 1 0 0 1-1-1z"/>,
  book:      <><path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v16H6.5A2.5 2.5 0 0 0 4 20.5z"/><path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20"/></>,
  // play uses fill so polygon is solid (same as Lucide Play icon)
  play:      <polygon points="6 4 20 12 6 20 6 4" fill="currentColor" stroke="none"/>,
  cert:      <><circle cx="12" cy="9" r="6"/><path d="m8.5 14-2 7L12 18l5.5 3-2-7"/></>,
  video:     <><path d="M15 10 19.55 7.93A1 1 0 0 1 21 8.82v6.36a1 1 0 0 1-1.45.89L15 14"/><rect x="3" y="7" width="12" height="10" rx="2"/></>,
  chart:     <><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6"/><rect x="12" y="8" width="3" height="10"/><rect x="17" y="5" width="3" height="13"/></>,
  qr:        <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3M20 14v3M17 17h4M14 21h3M21 21v-1"/></>,
  users:     <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></>,
  cog:       <><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></>,
  logout:    <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5M21 12H9"/></>,
  send:      <><path d="M22 2 11 13"/><path d="M22 2 15 22 11 13 2 9z"/></>,
  mic:       <><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 19v4M8 23h8"/></>,
  paperclip: <path d="M21.44 11.05 12.25 20.24a6 6 0 1 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/>,
  download:  <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
  upload:    <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>,
  plus:      <path d="M12 5v14M5 12h14"/>,
  close:     <path d="M18 6 6 18M6 6l12 12"/>,
  check:     <path d="M20 6 9 17l-5-5"/>,
  arrow_right: <path d="M5 12h14M13 5l7 7-7 7"/>,
  chevron_right: <path d="m9 18 6-6-6-6"/>,
  chevron_down:  <path d="m6 9 6 6 6-6"/>,
  chevron_left:  <path d="m15 18-6-6 6-6"/>,
  chevron_up:    <path d="m18 15-6-6-6 6"/>,
  caption:   <><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></>,
  fullscreen: <><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></>,
  pip:       <><rect x="2" y="2" width="20" height="15" rx="2"/><rect x="13" y="11" width="8" height="7" rx="1"/></>,
  settings:  <><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></>,
  flag:      <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></>,
  bookmark:  <path d="m19 21-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>,
  edit:      <><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></>,
  trash:     <><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></>,
  ai:        <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8"/>,
  filter:    <path d="M22 3H2l8 9.46V19l4 2v-8.54z"/>,
  globe:     <><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
  speed:     <><path d="M12 20h-7a2 2 0 0 1-2-2v-1"/><path d="M12 6v.01"/><path d="M3.05 11a10 10 0 1 1 .64 4"/><path d="M12 12l-4 4"/></>,
  hand:      <><path d="M18 11V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2"/><path d="M14 10V4a2 2 0 0 0-2-2 2 2 0 0 0-2 2v2"/><path d="M10 10.5V6a2 2 0 0 0-2-2 2 2 0 0 0-2 2v8"/><path d="M18 11a2 2 0 1 1 4 0v3a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/></>,
  copy:      <><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></>,
  link:      <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></>,
  refresh:   <><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></>,
  device:    <><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/></>,
  star:      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>,
  alert:     <><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3z"/><path d="M12 9v4M12 17h.01"/></>,
  doc:       <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></>,
  cam:       <><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></>,
  thumb_up:  <><path d="M7 10v12"/><path d="M15 5.88L14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88z"/></>,
  thumb_down:<><path d="M17 14V2"/><path d="M9 18.12L10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88z"/></>,
  pin:       <><line x1="12" y1="17" x2="12" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17z"/></>,
  calendar:  <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></>,
  shield:    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
  compass:   <><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></>,
  anchor:    <><circle cx="12" cy="5" r="3"/><line x1="12" y1="8" x2="12" y2="22"/><path d="M5 15H2a10 10 0 0 0 20 0h-3"/></>,
  lock:      <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
  map_pin:   <><path d="M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></>,
  clock:     <><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></>,
  // Extra icons used in other screens
  eye:       <><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></>,
  eye_off:   <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>,
  mail:      <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>,
  info:      <><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></>,
  external:  <><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></>,
  layers:    <><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></>,
  grid:      <><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></>,
  list:      <><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>,
  user:      <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  trending_up: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>,
  minus:     <line x1="5" y1="12" x2="19" y2="12"/>,
  more:      <><circle cx="12" cy="12" r="1"/><circle cx="12" cy="5" r="1"/><circle cx="12" cy="19" r="1"/></>,
  more_h:    <><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></>,
  // Ikon khusus menu sidebar — dibedakan supaya tiap menu punya tanda sendiri.
  key:       <><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></>,
  pie:       <><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></>,
  sun:       <><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></>,
  moon:      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>,
  monitor:   <><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></>,
  user_plus: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></>,
}

// Icon component — wraps SVG content with consistent stroke props
function Icon({ name, size = 16, className = "", color = "currentColor", style: customStyle }) {
  const content = ICONS[name]
  if (!content) return null
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flexShrink: 0, display: 'inline-block', verticalAlign: 'middle', ...customStyle }}
    >
      {content}
    </svg>
  )
}

// -- i18n -----------------------------------------------------
const I18N = {
  id: {
    // Neutral defaults, shown only until branding loads or when an institution
    // leaves the field blank. They must not name any one institution — these
    // used to say "Pelatihan OPRC" / "OSCT", which every other institution on
    // the platform then saw as the product's name.
    appName: "Gladhy",
    appNameSub: "Platform Pelatihan & Sertifikasi",
    // nav
    home: "Beranda",
    courses: "Kursus",
    myCourses: "Kursus Saya",
    courseLib: "Materi Kursus",
    liveSessions: "Sesi Live",
    schedule: "Jadwal",
    videos: "Video",
    aiAssistant: "Asisten AI",
    certificates: "Sertifikat",
    progress: "Progres",
    analytics: "Analitik",
    participants: "Peserta",
    users: "Pengguna",
    reports: "Laporan",
    settings: "Pengaturan",
    qrAttendance: "Absensi",
    qrLogin: "Masuk via QR",
    knowledgeBase: "Knowledge Base",
    pretest: "Pretest",
    posttest: "Posttest",
    learningGain: "Learning Gain",
    claimCert: "Klaim Sertifikat",
    sendCode: "Kirim Kode",
    studio: "Studio Trainer",
    organization: "Organisasi",
    // Pengelompokan sidebar berdasarkan alur kerja
    navTeaching: "Pembelajaran",
    navContent: "Konten",
    navPeople: "Peserta & Sertifikat",
    navInsights: "Analitik & Umpan Balik",
    navTraining: "Pelatihan",
    navAttendance: "Absensi",
    navEmails: "Email Peserta",
    navManageCourses: "Kelola Kursus",
    navManagePrograms: "Kelola Program",
    navTestEditor: "Editor Tes",
    navEnrollment: "Enrollment",
    navAppraisals: "Penilaian Kinerja",
    navInstitutions: "Lembaga",
    navCollapse: "Ciutkan menu",
    navExpand: "Perluas menu",
    // Kartu konteks program + kelas di sidebar
    ctxProgram: "Program",
    ctxClass: "Kelas",
    ctxSelectProgram: "Pilih program",
    ctxAllClasses: "Semua kelas",
    ctxAllClassesSub: "Seluruh angkatan program ini",
    ctxPublic: "Publik",
    ctxParticipants: "peserta",
    ctxChange: "Ganti program atau kelas",
    // Favorit sidebar & pencarian menu (Ctrl+K)
    navFavorites: "Favorit",
    navFavAdd: "Tambah ke favorit",
    navFavRemove: "Hapus dari favorit",
    gsMenu: "Menu",
    gsPlaceholderAdmin: "Cari menu, program, kursus…",
    gsPlaceholderMenu: "Cari menu…",
    // Arti angka di menu sidebar (badge "menunggu tindakan")
    // Tema tampilan
    themeLabel: "Tampilan",
    themeSystem: "Ikuti sistem",
    themeLight: "Terang",
    themeDark: "Gelap",
    badgeCertSend: "peserta layak belum dikirimi kode",
    badgeDocLibrary: "dokumen menunggu persetujuan",
    badgePenilaian: "penilaian lewat tenggat",
    logout: "Keluar",
    help: "Bantuan",
    notifications: "Notifikasi",
    // common
    search: "Cari kursus, video, peserta...",
    welcome: "Selamat datang kembali",
    continue: "Lanjut",
    continueLearning: "Lanjutkan belajar",
    viewAll: "Lihat semua",
    seeAll: "Lihat semua",
    download: "Download",
    join: "Gabung",
    start: "Mulai",
    resume: "Lanjut",
    enroll: "Daftar",
    submit: "Kirim",
    save: "Simpan",
    cancel: "Batal",
    next: "Selanjutnya",
    back: "Kembali",
    edit: "Ubah",
    moreInfo: "Detail",
    today: "Hari ini",
    yesterday: "Kemarin",
    tomorrow: "Besok",
    thisWeek: "Minggu ini",
    completed: "Selesai",
    inProgress: "Sedang berlangsung",
    notStarted: "Belum mulai",
    overdue: "Terlambat",
    upcoming: "Akan datang",
    scheduled: "Terjadwal",
    live: "Live",
    new: "Baru",
    minutes: "menit",
    hours: "jam",
    days: "hari",
    of: "dari",
    // login
    signIn: "Masuk",
    signInGoogle: "Masuk dengan Google",
    signInMicrosoft: "Masuk dengan Microsoft",
    email: "Email",
    password: "Password",
    forgotPassword: "Lupa password?",
    rememberMe: "Ingat saya selama 30 hari",
    or: "atau",
    loginTitle: "Masuk ke akun Anda",
    loginSubtitle: "Masuk untuk melanjutkan pelatihan dan sertifikasi Anda.",
    loginLeftTitle: "Tanggap tumpahan minyak,\nterstandar IMO.",
    loginLeftSub: "Single source of truth untuk pelatihan, presensi, dan sertifikasi.",
    // dashboards
    yourLearningPath: "Jalur belajar Anda",
    nextSession: "Sesi selanjutnya",
    activeCourse: "Kursus aktif",
    avgWatchTime: "Rata-rata watch-time",
    attendanceRate: "Tingkat kehadiran",
    quizAverage: "Rata-rata nilai kuis",
    upcomingSessions: "Sesi akan datang",
    recentVideos: "Video terbaru",
    yourCertificates: "Sertifikat Anda",
    classes: "Kelas",
    activeParticipants: "Peserta aktif",
    completionRate: "Tingkat penyelesaian",
    awaitingReview: "Menunggu review",
    classRoster: "Daftar peserta kelas",
    upcomingClasses: "Kelas akan datang",
    todaysSchedule: "Jadwal hari ini",
    // admin
    totalUsers: "Total pengguna",
    totalCourses: "Total kursus",
    certificatesIssued: "Sertifikat diterbitkan",
    monthlyActiveUsers: "MAU",
    complianceStatus: "Status kepatuhan",
    coursePerformance: "Performa kursus",
    enrollmentTrend: "Tren Pendaftaran",
    auditTrail: "Riwayat audit",
    // course
    modules: "Modul",
    lessons: "Pelajaran",
    duration: "Durasi",
    instructor: "Instruktur",
    level: "Level",
    schedule2: "Jadwal",
    syllabus: "Daftar Materi",
    overview: "Ringkasan",
    materials: "Materi",
    discussions: "Diskusi",
    // player
    chapters: "Bab",
    transcript: "Transkrip",
    notes: "Catatan",
    bookmarks: "Bookmark",
    addNote: "Tambah catatan",
    captionOn: "Subtitle ID",
    // qr
    generateQR: "Buat QR Absensi",
    scanQR: "Scan QR",
    sessionAttendance: "Absensi Sesi",
    rotatesIn: "Ganti dalam",
    scanThisCode: "Scan kode ini",
    realtimeAttendance: "Kehadiran real-time",
    // ai
    askAnything: "Tanya apa saja tentang materi pelatihan...",
    aiDisclaimer: "Asisten ini hanya menjawab dari dokumen yang diupload ke Knowledge Base. Hubungi trainer untuk pertanyaan di luar cakupan.",
    suggestedQuestions: "Saran pertanyaan",
    // certificate
    verifyCert: "Verifikasi Sertifikat",
    certNumber: "Nomor Sertifikat",
    issuedTo: "Diterbitkan untuk",
    issuedOn: "Tanggal terbit",
    validUntil: "Berlaku sampai",
    issuedBy: "Diterbitkan oleh",
    valid: "VALID",
    // live
    leaveSession: "Keluar sesi",
    raiseHand: "Angkat tangan",
    qa: "Tanya Jawab",
    polls: "Polling",
    chat: "Chat",
    participants2: "Peserta",
    viewers: "penonton",
    sendMessage: "Kirim pesan...",
    // assignment
    submission: "Kiriman",
    rubric: "Rubrik penilaian",
    timestamp: "Timestamp",
    grade: "Nilai",
    feedback: "Feedback",
    requestRevision: "Minta revisi",
    finalize: "Selesaikan",
    record: "Rekam",
    uploadVideo: "Upload video",
    // analytics
    progressByLevel: "Progres per Level",
    dropOffRate: "Drop-off video",
    topRewatched: "Paling sering diulang",
    bandwidth: "Bandwidth",
    // ── FR-31 Doc Library / FR-32 Training Hub / shared ──
    loadingText: "Memuat…",
    cmsLibraryEyebrow: "Dokumen",
    cmsTitle: "Dokumen & Materi",
    cmsDescParticipant: "Materi referensi yang telah disetujui trainer & admin.",
    cmsDescStaff: "Unggah, review, dan kelola materi referensi program.",
    cmsSearch: "Cari dokumen…",
    cmsNoDocs: "Belum ada dokumen tersedia.",
    cmsTabDocs: "Dokumen",
    cmsTabReview: "Antrean Review",
    cmsTabModules: "Modul",
    cmsUpload: "Unggah dokumen",
    cmsModule: "Modul",
    cmsSelectModule: "Pilih modul…",
    cmsNameOptional: "Nama (opsional)",
    cmsDefaultFileName: "Default: nama file",
    cmsFileMax: "Berkas (≤25MB)",
    cmsUploading: "Mengunggah…",
    cmsUploadBtn: "Unggah",
    cmsNoModulesAskAdmin: "Belum ada modul. Minta admin membuat modul terlebih dahulu.",
    cmsDocCol: "Dokumen",
    cmsTypeCol: "Tipe",
    cmsSizeCol: "Ukuran",
    cmsStatusCol: "Status",
    cmsUploaderCol: "Pengunggah",
    cmsActionsCol: "Aksi",
    cmsNoDocuments: "Tidak ada dokumen.",
    cmsSubmit: "Submit",
    cmsNewVersion: "Versi baru",
    cmsApprove: "Setujui",
    cmsReject: "Tolak",
    cmsRejectPrompt: "Alasan penolakan:",
    cmsManageModules: "Kelola modul",
    cmsNoModules: "Belum ada modul.",
    cmsSeedDefaults: "Buat modul default",
    cmsNewModuleName: "Nama modul baru",
    cmsAdd: "Tambah",
    cmsDeleteModuleConfirm: "Hapus modul ini?",
    cmsModuleHasDocs: "Modul masih berisi dokumen",
    cmsFeatured: "Unggulan",
    cmsDownload: "Unduh",
    cmsDocsWord: "dokumen",
    cmsDocsAbbr: "dok",
    stDraft: "Draft",
    stPendingReview: "Menunggu review",
    stApproved: "Disetujui",
    stRejected: "Ditolak",
    thEyebrowOps: "Operasional",
    thTitle: "Training Hub",
    thDescStaff: "Ringkasan aktivitas sesi pelatihan secara real-time.",
    thActiveTrainings: "Pelatihan aktif",
    thActiveHint: "live / 7 hari ke depan",
    thTotalParticipants: "Total peserta",
    thCompletionRate: "Tingkat penyelesaian",
    thAvgRating: "Rata-rata rating",
    thRatingsWord: "rating",
    thTrainings: "Daftar sesi",
    thSession: "Sesi",
    thTrainer: "Trainer",
    thMode: "Mode",
    thAttended: "Hadir",
    thStatus: "Status",
    thConference: "Konferensi",
    thNoSessions: "Belum ada sesi.",
    thActivity7d: "Aktivitas 7 hari",
    thActivitySub: "Presensi + lesson selesai",
    thTopModules: "Modul teratas",
    thNoModules: "Belum ada modul.",
    thLessonsWord: "pelajaran",
    stLive: "Live",
    stScheduled: "Terjadwal",
    stCompleted: "Selesai",
    confConnected: "Terhubung",
    confReady: "Siap",
    confEnded: "Selesai",
    confMissing: "URL hilang",
    thMySessions: "Sesi Saya",
    thUpcoming: "Sesi mendatang",
    thNoUpcoming: "Tidak ada sesi mendatang.",
    thGoing: "Hadir",
    thNotGoing: "Tidak",
    thJoin: "Gabung",
    thHistory: "Riwayat sesi",
    thNoHistory: "Belum ada sesi yang dihadiri.",
    thRated: "Dinilai",
    thRate: "Beri rating",
    thComment: "Komentar (opsional)",
    thSubmit: "Kirim",
    thTrainers: "Trainer program",
    // CMS design re-skin
    cmsContentMgmt: "Manajemen Konten",
    cmsHeaderDesc: "Unggah, review, dan kelola dokumen pelatihan per modul.",
    cmsStatTotal: "Total file",
    cmsStatPending: "Menunggu review",
    cmsStatApproved: "Disetujui",
    cmsStatStorage: "Penyimpanan",
    cmsAllModules: "Semua modul",
    cmsFilesWord: "file",
    cmsModulesWord: "modul",
    cmsAllTypes: "Semua tipe",
    cmsAllStatuses: "Semua status",
    cmsPendingAlert: "dokumen menunggu review.",
    cmsShowPending: "Lihat pending →",
    cmsNoDocsHere: "Belum ada dokumen di sini.",
    cmsUploadFirstHint: "Unggah dokumen pertama untuk memulai.",
    cmsAllLevelDocs: "Semua dokumen",
    cmsTargetModule: "Modul tujuan",
    cmsDropFiles: "Letakkan file di sini atau telusuri",
    cmsDropHint: "PDF, DOCX, PPTX, XLSX · maks 25 MB per file",
    cmsDocTitleLabel: "Judul dokumen",
    cmsDocTitleHint: "opsional — default nama file",
    cmsSaveDraft: "Simpan draft",
    cmsSubmitReview: "Kirim untuk review",
    cmsExceeds: "Melebihi 25 MB",
    cmsViewDetails: "Lihat detail",
    cmsUploadedBy: "Diunggah oleh",
    cmsVersionLabel: "Versi",
    cmsByWord: "oleh",
    cmsClose: "Tutup",
    cmsDelete: "Hapus",
    // Training Hub design re-skin
    thOverview: "Ikhtisar",
    thModulesTab: "Modul",
    thAllOperational: "Semua sistem operasional",
    thThisWeek: "minggu ini",
    thTrainingsToday: "sesi pelatihan",
    thLiveNow: "live",
    thViewAll: "Lihat semua →",
    thManage: "Kelola",
    thRemote: "daring",
    thNewModule: "Modul baru",
    thTotalLessons: "total pelajaran",
    thViewModule: "Lihat modul →",
    thLiveBanner: "Sedang live",
    thJoinStream: "Gabung",
    thWatchLive: "Tonton live",
    thAvailableWhenLive: "Tersedia saat live",
    thSessionEnded: "Sesi selesai",
    thAllSessions: "Semua sesi",
    thParticipantNote: "Sebagai peserta, Anda dapat bergabung ke sesi yang sedang live via tautan Zoom. Sesi terjadwal terbuka saat trainer memulai. Kehadiran dihitung via QR check-in. Pada sesi Zoom yang dibuat otomatis, kehadiran juga dihitung dari lama Anda mengikuti meeting.",
    confConnectedShort: "Terhubung",
    confReadyShort: "Siap",
    confEndedShort: "Selesai",
    // ── Live translation subtitles ──
    subtitleToggle: "Terjemahan Live",
    subtitleUnsupported: "Browser tidak mendukung (butuh Chrome/Edge)",
    subtitleMicDenied: "Akses mikrofon ditolak",
    subtitleConnecting: "Menghubungkan...",
    subtitleDisconnected: "Terputus, mencoba kembali...",
    subtitleFrom: "Dari",
    // ── Cert Batch (Sprint A) ──
    cbNav: "Penerbitan Sertifikat",
    cbEyebrow: "Sertifikasi",
    cbTitle: "Penerbitan Sertifikat",
    cbDesc: "Kelola batch penerbitan sertifikat peserta.",
    cbCreate: "Buat Training",
    cbPending: "Pending",
    cbMore: "Lebih Banyak",
    cbEmpty: "Belum ada training.",
    cbTabLabel: "Kredensial",
    // ── Feedback ──
    fbNav: "Feedback",
    fbEyebrow: "Suara Peserta",
    fbTitle: "Feedback",
    fbDescParticipant: "Sampaikan masukan Anda tentang pelatihan. Hanya admin yang dapat melihatnya.",
    fbDescAdmin: "Masukan dari peserta. Tinjau dan ekspor sebagai laporan.",
    fbCategory: "Kategori",
    fbCatGENERAL: "Umum",
    fbCatCONTENT: "Materi",
    fbCatTRAINER: "Trainer",
    fbCatPLATFORM: "Platform",
    fbCatSUGGESTION: "Saran",
    fbRating: "Penilaian (opsional)",
    fbSubject: "Judul (opsional)",
    fbSubjectPlaceholder: "Ringkasan singkat",
    fbMessage: "Pesan",
    fbMessagePlaceholder: "Tuliskan masukan Anda…",
    fbSend: "Kirim Feedback",
    fbSending: "Mengirim…",
    fbThanks: "Terima kasih atas masukan Anda!",
    fbMineTitle: "Masukan Anda sebelumnya",
    fbNoneMine: "Anda belum mengirim feedback.",
    fbAdminTotal: "Total masukan",
    fbAdminAvgRating: "Rata-rata penilaian",
    fbAdminRatingsWord: "penilaian",
    fbExport: "Ekspor",
    fbNoFeedback: "Belum ada feedback.",
    fbColDate: "Tanggal",
    fbColParticipant: "Peserta",
    fbColCategory: "Kategori",
    fbColRating: "Nilai",
    fbColFeedback: "Feedback",
    fbAdminOnly: "Halaman ini hanya untuk admin.",
    // ── Feedback NPS / aspects ──
    fbNps: "Seberapa besar kemungkinan Anda merekomendasikan pelatihan ini ke rekan?",
    fbNpsLow: "Sangat tidak mungkin",
    fbNpsHigh: "Sangat mungkin",
    fbAspects: "Apa yang sudah baik?",
    fbAspectsHint: "(pilih beberapa)",
    fbImprove: "Apa yang bisa diperbaiki?",
    fbImproveHint: "(opsional, anonim)",
    fbImprovePlaceholder: "Mis. waktu drill terasa terburu-buru di awal…",
    fbAiTopic: "Topik yang ingin dibahas Asisten AI?",
    fbAiTopicPlaceholder: "Mis. alur persetujuan dispersan",
    fbPrivacy: "Tanggapan diagregasi. Trainer & admin melihat rating dan tema, bukan identitas Anda.",
    fbAspectContent: "Kualitas materi",
    fbAspectTrainer: "Kejelasan trainer",
    fbAspectPace: "Tempo",
    fbAspectMaterials: "Bahan/materi",
    fbAspectDrills: "Latihan praktik",
    fbAspectVenue: "Tempat & peralatan",
    fbAspectDiscussion: "Diskusi kelompok",
    fbAdminNps: "Skor NPS",
    fbAdminPromoters: "Promotor",
    fbAdminDetractors: "Detraktor",
    fbAspectsTitle: "Aspek yang dihargai",
    // ── Dashboard charts ──
    chartPerformance: "Grafik Kinerja",
    chartCompletion: "Penyelesaian Modul",
    chartPrePost: "Pretest vs Posttest",
    chartPretest: "Pretest",
    chartPosttest: "Posttest",
    chartPerCourse: "Penyelesaian per Kursus",
    chartPerProgram: "Peserta per Program",
    chartParticipants: "Peserta",
    chartCertified: "Lulus",
    chartNoData: "Belum ada data untuk ditampilkan.",
    chartWatchSessions: "Sesi tonton",
    chartTests: "Tes",
    chartViewChart: "Grafik",
    chartViewTable: "Tabel",
    chartDate: "Tanggal",
    chartValue: "Nilai",
  },
  en: {
    appName: "Gladhy",
    appNameSub: "Training & Certification Platform",
    home: "Home",
    courses: "Courses",
    myCourses: "My Courses",
    courseLib: "Course Library",
    liveSessions: "Live Sessions",
    schedule: "Schedule",
    videos: "Video Library",
    aiAssistant: "AI Assistant",
    certificates: "Certificates",
    progress: "Progress",
    analytics: "Analytics",
    participants: "Participants",
    users: "Users",
    reports: "Reports",
    settings: "Settings",
    qrAttendance: "Attendance",
    qrLogin: "Sign in with QR",
    knowledgeBase: "Knowledge Base",
    pretest: "Pretest",
    posttest: "Posttest",
    learningGain: "Learning Gain",
    claimCert: "Claim Certificate",
    sendCode: "Send Code",
    studio: "Trainer Studio",
    organization: "Organization",
    // Sidebar groups by workflow
    navTeaching: "Learning",
    navContent: "Content",
    navPeople: "People & Certificates",
    navInsights: "Insights & Feedback",
    navTraining: "Training",
    navAttendance: "Attendance",
    navEmails: "Emails",
    navManageCourses: "Manage Courses",
    navManagePrograms: "Manage Programs",
    navTestEditor: "Test Editor",
    navEnrollment: "Enrollment",
    navAppraisals: "Appraisals",
    navInstitutions: "Institutions",
    navCollapse: "Collapse menu",
    navExpand: "Expand menu",
    // Program + class context card in the sidebar
    ctxProgram: "Program",
    ctxClass: "Class",
    ctxSelectProgram: "Select program",
    ctxAllClasses: "All classes",
    ctxAllClassesSub: "Every class in this program",
    ctxPublic: "Public",
    ctxParticipants: "participants",
    ctxChange: "Change program or class",
    // Sidebar favorites & menu search (Ctrl+K)
    navFavorites: "Favorites",
    navFavAdd: "Add to favorites",
    navFavRemove: "Remove from favorites",
    gsMenu: "Menu",
    gsPlaceholderAdmin: "Search menu, programs, courses…",
    gsPlaceholderMenu: "Search menu…",
    // Meaning of the numbers on sidebar menu items ("needs action" badges)
    // Appearance
    themeLabel: "Appearance",
    themeSystem: "System",
    themeLight: "Light",
    themeDark: "Dark",
    badgeCertSend: "eligible participants not sent a code yet",
    badgeDocLibrary: "documents awaiting approval",
    badgePenilaian: "overdue appraisals",
    logout: "Sign out",
    help: "Help",
    notifications: "Notifications",
    search: "Search courses, videos, participants...",
    welcome: "Welcome back",
    continue: "Continue",
    continueLearning: "Continue learning",
    viewAll: "View all",
    seeAll: "See all",
    download: "Download",
    join: "Join",
    start: "Start",
    resume: "Resume",
    enroll: "Enroll",
    submit: "Submit",
    save: "Save",
    cancel: "Cancel",
    next: "Next",
    back: "Back",
    edit: "Edit",
    moreInfo: "Details",
    today: "Today",
    yesterday: "Yesterday",
    tomorrow: "Tomorrow",
    thisWeek: "This week",
    completed: "Completed",
    inProgress: "In progress",
    notStarted: "Not started",
    overdue: "Overdue",
    upcoming: "Upcoming",
    scheduled: "Scheduled",
    live: "Live",
    new: "New",
    minutes: "min",
    hours: "hrs",
    days: "days",
    of: "of",
    signIn: "Sign in",
    signInGoogle: "Sign in with Google",
    signInMicrosoft: "Sign in with Microsoft",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot password?",
    rememberMe: "Remember me for 30 days",
    or: "or",
    loginTitle: "Sign in to your account",
    loginSubtitle: "Sign in to continue your training and certification.",
    loginLeftTitle: "Oil spill response,\nstandardized to IMO.",
    loginLeftSub: "Single source of truth for training, attendance, and certification.",
    yourLearningPath: "Your learning path",
    nextSession: "Next session",
    activeCourse: "Active course",
    avgWatchTime: "Avg. watch-time",
    attendanceRate: "Attendance rate",
    quizAverage: "Quiz average",
    upcomingSessions: "Upcoming sessions",
    recentVideos: "Recent videos",
    yourCertificates: "Your certificates",
    classes: "Classes",
    activeParticipants: "Active participants",
    completionRate: "Completion rate",
    awaitingReview: "Awaiting review",
    classRoster: "Class roster",
    upcomingClasses: "Upcoming classes",
    todaysSchedule: "Today's schedule",
    totalUsers: "Total users",
    totalCourses: "Total courses",
    certificatesIssued: "Certificates issued",
    monthlyActiveUsers: "MAU",
    complianceStatus: "Compliance status",
    coursePerformance: "Course performance",
    enrollmentTrend: "Enrollment trend",
    auditTrail: "Audit trail",
    modules: "Modules",
    lessons: "Lessons",
    duration: "Duration",
    instructor: "Instructor",
    level: "Level",
    schedule2: "Schedule",
    syllabus: "Syllabus",
    overview: "Overview",
    materials: "Materials",
    discussions: "Discussions",
    chapters: "Chapters",
    transcript: "Transcript",
    notes: "Notes",
    bookmarks: "Bookmarks",
    addNote: "Add note",
    captionOn: "Caption ID",
    generateQR: "Generate Attendance QR",
    scanQR: "Scan QR",
    sessionAttendance: "Session Attendance",
    rotatesIn: "Rotates in",
    scanThisCode: "Scan this code",
    realtimeAttendance: "Real-time attendance",
    askAnything: "Ask anything about your training material...",
    aiDisclaimer: "This assistant only answers from documents uploaded to the Knowledge Base. Contact your trainer for questions outside scope.",
    suggestedQuestions: "Suggested questions",
    verifyCert: "Verify certificate",
    certNumber: "Certificate No.",
    issuedTo: "Issued to",
    issuedOn: "Issued on",
    validUntil: "Valid until",
    issuedBy: "Issued by",
    valid: "VALID",
    leaveSession: "Leave session",
    raiseHand: "Raise hand",
    qa: "Q&A",
    polls: "Polls",
    chat: "Chat",
    participants2: "Participants",
    viewers: "viewers",
    sendMessage: "Send a message...",
    submission: "Submission",
    rubric: "Grading rubric",
    timestamp: "Timestamp",
    grade: "Score",
    feedback: "Feedback",
    requestRevision: "Request revision",
    finalize: "Finalize",
    record: "Record",
    uploadVideo: "Upload video",
    progressByLevel: "Progress by level",
    dropOffRate: "Video drop-off",
    topRewatched: "Most rewatched",
    bandwidth: "Bandwidth",
    // ── FR-31 Doc Library / FR-32 Training Hub / shared ──
    loadingText: "Loading…",
    cmsLibraryEyebrow: "Library",
    cmsTitle: "Doc Library",
    cmsDescParticipant: "Approved reference materials from trainers & admins.",
    cmsDescStaff: "Upload, review, and manage program reference materials.",
    cmsSearch: "Search docs…",
    cmsNoDocs: "No documents available yet.",
    cmsTabDocs: "Documents",
    cmsTabReview: "Review Queue",
    cmsTabModules: "Modules",
    cmsUpload: "Upload document",
    cmsModule: "Module",
    cmsSelectModule: "Select module…",
    cmsNameOptional: "Name (optional)",
    cmsDefaultFileName: "Default: file name",
    cmsFileMax: "File (≤25MB)",
    cmsUploading: "Uploading…",
    cmsUploadBtn: "Upload",
    cmsNoModulesAskAdmin: "No modules yet. Ask an admin to create one first.",
    cmsDocCol: "Document",
    cmsTypeCol: "Type",
    cmsSizeCol: "Size",
    cmsStatusCol: "Status",
    cmsUploaderCol: "Uploader",
    cmsActionsCol: "Actions",
    cmsNoDocuments: "No documents.",
    cmsSubmit: "Submit",
    cmsNewVersion: "New version",
    cmsApprove: "Approve",
    cmsReject: "Reject",
    cmsRejectPrompt: "Rejection reason:",
    cmsManageModules: "Manage modules",
    cmsNoModules: "No modules yet.",
    cmsSeedDefaults: "Seed default modules",
    cmsNewModuleName: "New module name",
    cmsAdd: "Add",
    cmsDeleteModuleConfirm: "Delete this module?",
    cmsModuleHasDocs: "Module has documents",
    cmsFeatured: "Featured",
    cmsDownload: "Download",
    cmsDocsWord: "docs",
    cmsDocsAbbr: "docs",
    stDraft: "Draft",
    stPendingReview: "Pending review",
    stApproved: "Approved",
    stRejected: "Rejected",
    thEyebrowOps: "Operations",
    thTitle: "Training Hub",
    thDescStaff: "Real-time summary of training session activity.",
    thActiveTrainings: "Active trainings",
    thActiveHint: "live / next 7 days",
    thTotalParticipants: "Total participants",
    thCompletionRate: "Completion rate",
    thAvgRating: "Avg. rating",
    thRatingsWord: "ratings",
    thTrainings: "Trainings",
    thSession: "Session",
    thTrainer: "Trainer",
    thMode: "Mode",
    thAttended: "Attended",
    thStatus: "Status",
    thConference: "Conference",
    thNoSessions: "No sessions yet.",
    thActivity7d: "7-day activity",
    thActivitySub: "Scans + lesson completions",
    thTopModules: "Top modules",
    thNoModules: "No modules.",
    thLessonsWord: "lessons",
    stLive: "Live",
    stScheduled: "Scheduled",
    stCompleted: "Completed",
    confConnected: "Connected",
    confReady: "Ready",
    confEnded: "Ended",
    confMissing: "Missing URL",
    thMySessions: "My Sessions",
    thUpcoming: "Upcoming sessions",
    thNoUpcoming: "No upcoming sessions.",
    thGoing: "Going",
    thNotGoing: "Not going",
    thJoin: "Join",
    thHistory: "Session history",
    thNoHistory: "No attended sessions yet.",
    thRated: "Rated",
    thRate: "Rate",
    thComment: "Comment (optional)",
    thSubmit: "Submit",
    thTrainers: "Program trainers",
    // CMS design re-skin
    cmsContentMgmt: "Content Management",
    cmsHeaderDesc: "Upload, review, and manage training documents by module.",
    cmsStatTotal: "Total files",
    cmsStatPending: "Pending review",
    cmsStatApproved: "Approved",
    cmsStatStorage: "Storage used",
    cmsAllModules: "All modules",
    cmsFilesWord: "files",
    cmsModulesWord: "modules",
    cmsAllTypes: "All types",
    cmsAllStatuses: "All statuses",
    cmsPendingAlert: "documents awaiting review.",
    cmsShowPending: "Show pending →",
    cmsNoDocsHere: "No documents here yet.",
    cmsUploadFirstHint: "Upload your first document to get started.",
    cmsAllLevelDocs: "All documents",
    cmsTargetModule: "Target module",
    cmsDropFiles: "Drop a file here or browse",
    cmsDropHint: "PDF, DOCX, PPTX, XLSX · max 25 MB per file",
    cmsDocTitleLabel: "Document title",
    cmsDocTitleHint: "optional — defaults to file name",
    cmsSaveDraft: "Save as draft",
    cmsSubmitReview: "Submit for review",
    cmsExceeds: "Exceeds 25 MB",
    cmsViewDetails: "View details",
    cmsUploadedBy: "Uploaded by",
    cmsVersionLabel: "Version",
    cmsByWord: "by",
    cmsClose: "Close",
    cmsDelete: "Delete",
    // Training Hub design re-skin
    thOverview: "Overview",
    thModulesTab: "Modules",
    thAllOperational: "All systems operational",
    thThisWeek: "this week",
    thTrainingsToday: "training sessions",
    thLiveNow: "live",
    thViewAll: "View all →",
    thManage: "Manage",
    thRemote: "remote",
    thNewModule: "New module",
    thTotalLessons: "total lessons",
    thViewModule: "View module →",
    thLiveBanner: "Live now",
    thJoinStream: "Join",
    thWatchLive: "Watch live",
    thAvailableWhenLive: "Available when live",
    thSessionEnded: "Session ended",
    thAllSessions: "All sessions",
    thParticipantNote: "As a participant, you can join sessions that are live now via the Zoom link. Scheduled sessions unlock when the trainer starts. Attendance is tracked via QR check-in. For automatically created Zoom sessions, it is also based on how long you stay in the meeting.",
    confConnectedShort: "Connected",
    confReadyShort: "Ready",
    confEndedShort: "Ended",
    // ── Live translation subtitles ──
    subtitleToggle: "Live Translation",
    subtitleUnsupported: "Browser not supported (requires Chrome/Edge)",
    subtitleMicDenied: "Microphone access denied",
    subtitleConnecting: "Connecting...",
    subtitleDisconnected: "Disconnected, retrying...",
    subtitleFrom: "From",
    // ── Cert Batch (Sprint A) ──
    cbNav: "Credential Issuance",
    cbEyebrow: "Credentials",
    cbTitle: "Credential Issuance",
    cbDesc: "Manage certificate issuance batches.",
    cbCreate: "Create Training",
    cbPending: "Pending Trainings",
    cbMore: "More Trainings",
    cbEmpty: "No trainings yet.",
    cbTabLabel: "Credentials",
    // ── Feedback ──
    fbNav: "Feedback",
    fbEyebrow: "Participant Voice",
    fbTitle: "Feedback",
    fbDescParticipant: "Share your thoughts about the training. Only admins can see it.",
    fbDescAdmin: "Feedback from participants. Review and export as a report.",
    fbCategory: "Category",
    fbCatGENERAL: "General",
    fbCatCONTENT: "Content",
    fbCatTRAINER: "Trainer",
    fbCatPLATFORM: "Platform",
    fbCatSUGGESTION: "Suggestion",
    fbRating: "Rating (optional)",
    fbSubject: "Subject (optional)",
    fbSubjectPlaceholder: "A short summary",
    fbMessage: "Message",
    fbMessagePlaceholder: "Write your feedback…",
    fbSend: "Send Feedback",
    fbSending: "Sending…",
    fbThanks: "Thanks for your feedback!",
    fbMineTitle: "Your previous feedback",
    fbNoneMine: "You haven't submitted any feedback yet.",
    fbAdminTotal: "Total feedback",
    fbAdminAvgRating: "Average rating",
    fbAdminRatingsWord: "ratings",
    fbExport: "Export",
    fbNoFeedback: "No feedback yet.",
    fbColDate: "Date",
    fbColParticipant: "Participant",
    fbColCategory: "Category",
    fbColRating: "Rating",
    fbColFeedback: "Feedback",
    fbAdminOnly: "This page is for admins only.",
    // ── Feedback NPS / aspects ──
    fbNps: "How likely are you to recommend this training to a colleague?",
    fbNpsLow: "Not at all likely",
    fbNpsHigh: "Extremely likely",
    fbAspects: "What worked well?",
    fbAspectsHint: "(select any)",
    fbImprove: "What could improve?",
    fbImproveHint: "(optional, anonymous)",
    fbImprovePlaceholder: "e.g. the drill timing felt rushed at the start…",
    fbAiTopic: "Any topic you'd like the AI Assistant to cover?",
    fbAiTopicPlaceholder: "e.g. dispersant approval workflow",
    fbPrivacy: "Responses are aggregated. Trainers & admins see ratings and themes, not your identity.",
    fbAspectContent: "Content quality",
    fbAspectTrainer: "Trainer clarity",
    fbAspectPace: "Pacing",
    fbAspectMaterials: "Materials",
    fbAspectDrills: "Hands-on drills",
    fbAspectVenue: "Venue & equipment",
    fbAspectDiscussion: "Group discussion",
    fbAdminNps: "NPS score",
    fbAdminPromoters: "Promoters",
    fbAdminDetractors: "Detractors",
    fbAspectsTitle: "Aspects appreciated",
    // ── Dashboard charts ──
    chartPerformance: "Performance Chart",
    chartCompletion: "Module Completion",
    chartPrePost: "Pretest vs Posttest",
    chartPretest: "Pretest",
    chartPosttest: "Posttest",
    chartPerCourse: "Completion per Course",
    chartPerProgram: "Participants per Program",
    chartParticipants: "Participants",
    chartCertified: "Certified",
    chartNoData: "No data to display yet.",
    chartWatchSessions: "Watch sessions",
    chartTests: "Tests",
    chartViewChart: "Chart",
    chartViewTable: "Table",
    chartDate: "Date",
    chartValue: "Value",
  },
}

function useT() {
  const { lang } = React.useContext(AppContext)
  return I18N[lang]
}

// -- Fake data ------------------------------------------------
const FAKE = {
  user: {
    participant: { name: "Budi Santoso", initials: "BS", org: "PT Pertamina Hulu Energi", role: "First Responder" },
    trainer: { name: "Capt. Rina Wijaya", initials: "RW", org: "Pusat Pelatihan Maritim Jakarta", role: "IMO Certified Trainer" },
    admin: { name: "Dr. Hadi Pratama", initials: "HP", org: "BPSDM Perhubungan", role: "Training Administrator" },
  },
  courses: [
    {
      id: "L1-2026-03",
      title: "OPRC Level 1 — First Responder",
      title_en: "OPRC Level 1 — First Responder",
      level: 1,
      instructor: "Capt. Rina Wijaya",
      progress: 64,
      modules: 6, lessons: 24,
      hours: 32,
      next: "Modul 3 · Pelajaran 5 · Teknik Containment Boom",
      next_en: "Module 3 · Lesson 5 · Boom Containment Technique",
      tag: "blue",
      cohort: "Batch Mei 2026",
      enrolled: 28,
    },
    {
      id: "L2-2026-04",
      title: "OPRC Level 2 — On-Scene Commander",
      title_en: "OPRC Level 2 — On-Scene Commander",
      level: 2,
      instructor: "Capt. Rina Wijaya",
      progress: 22,
      modules: 8, lessons: 36,
      hours: 48,
      next: "Modul 2 · Manajemen Insiden (ICS)",
      next_en: "Module 2 · Incident Command System (ICS)",
      tag: "teal",
      cohort: "Batch Apr 2026",
      enrolled: 18,
    },
    {
      id: "L3-2026-02",
      title: "OPRC Level 3 — Senior Management",
      title_en: "OPRC Level 3 — Senior Management",
      level: 3,
      instructor: "Dr. Hadi Pratama",
      progress: 100,
      modules: 5, lessons: 18,
      hours: 24,
      next: "Selesai · sertifikat tersedia",
      next_en: "Complete · certificate available",
      tag: "dark",
      cohort: "Batch Feb 2026",
      enrolled: 12,
    },
  ],
  upcoming: [
    { when: "Hari ini · 14:00", title: "Live: ICS Roles & Responsibilities", host: "Capt. Rina Wijaya", type: "live", in: "2 jam 14 menit", course: "OPRC Level 2" },
    { when: "Besok · 09:00", title: "Tatap muka: Drill Pemasangan Boom", host: "Capt. Rina Wijaya", type: "onsite", in: "—", course: "OPRC Level 1", location: "Pelabuhan Tanjung Priok" },
    { when: "Kam · 13:30", title: "Tabletop: Contingency Planning", host: "Dr. Hadi Pratama", type: "live", in: "—", course: "OPRC Level 3" },
  ],
  notifications: 4,
}

export { useIsMobile } from './useIsMobile.js'
export { Icon, ICONS, I18N, useT, FAKE }
